import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getValueCookie } from '../../services/cookieService';
import { getInformationWithDataV3 } from '../../services/CABE';
import { AiOutlineShopping, AiOutlineReload, AiOutlineFullscreen, AiOutlineFullscreenExit } from 'react-icons/ai';

// Isolated component (2026-09-23) — same pattern as InvoiceMonitorButton, for Sales Orders.
// Only render inside the SO V3 page.
const SO_ROUTES = ['/sales-orders-v3'];

const STATUS_LABEL = {
  pending: 'Pending verification',
  retrying: 'Retrying',
  confirmed: 'Confirmed in Sage',
  failed: 'Needs manual review',
  cancelled: 'Cancelled',
};

const STATUS_CLASS = {
  pending: 'secondary',
  retrying: 'warning',
  confirmed: 'success',
  failed: 'danger',
  cancelled: 'secondary',
};

const COMPANY_LABEL = { 3: 'Mayaland', 6: 'Altitude' };

const IN_PROGRESS_STATUSES = ['pending', 'retrying'];
const PANEL_WIDTH = 200; // TOOLBAR-2026-10-02 (client's ask, screenshots showed it still covering Unit Price/Total columns at 260) — was 340, then 260; content wraps onto more lines per record instead, by design (client's own call)
const COMPACT_LIST_HEIGHT = 190;
const COMPACT_ORDER_LIMIT = 4; // TOOLBAR-2026-10-02 (client's ask) — compact view was showing every order from the last 24h, scroll got "muy grande"; cap it, Expand still shows everything
const RECHECK_AFTER_SECONDS = 60; // must match RECHECK_AFTER_MS in api-portal-manager/src/v3_controllers/soMonitor.ts
const POLL_MS = 30000;

function formatRelativeTime(dateStr) {
  if (!dateStr) return 'never';
  const diffSec = Math.max(0, Math.round((Date.now() - new Date(dateStr).getTime()) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  return `${Math.round(diffMin / 60)}h ago`;
}

function getNextCheckLabel(leg) {
  if (!IN_PROGRESS_STATUSES.includes(leg.status)) return null;
  if (!leg.lastCheckedAt) return 'checking for the first time shortly';
  const secondsSinceChecked = Math.round((Date.now() - new Date(leg.lastCheckedAt).getTime()) / 1000);
  const remaining = RECHECK_AFTER_SECONDS - secondsSinceChecked;
  return remaining <= 0 ? 'eligible for next check now' : `earliest next check in ~${remaining}s`;
}

// Groups the flat (orderNo, idCompany) rows the backend returns into one card per orderNo,
// each holding up to 2 legs — an Altitude order shows both its Altitude and Mayaland-mirror
// legs together, a native Mayaland order just shows its one.
function groupByOrder(rows) {
  const map = new Map();
  for (const row of rows) {
    if (!map.has(row.orderNo)) map.set(row.orderNo, { orderNo: row.orderNo, createdAt: row.createdAt, legs: [] });
    map.get(row.orderNo).legs.push(row);
  }
  return Array.from(map.values()).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

export const SOMonitorButton = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [retryingKey, setRetryingKey] = useState(null);
  const intervalRef = useRef(null);

  const userName = getValueCookie('userName');
  const onSOPage = SO_ROUTES.includes(location.pathname);

  const fetchOrders = useCallback(async () => {
    if (!userName) return;
    setLoading(true);
    try {
      const response = await getInformationWithDataV3('/v3/so/monitor/my-status', { userName });
      if (response && response.status && response.status.code === 1 && Array.isArray(response.data)) {
        setRows(response.data);
      }
    } catch (err) {
      console.log('[SOMonitorButton] fetch error:', err);
    } finally {
      setLoading(false);
      setLastFetchedAt(new Date());
    }
  }, [userName]);

  useEffect(() => {
    if (!userName || !onSOPage) return;
    fetchOrders();
    intervalRef.current = setInterval(fetchOrders, POLL_MS);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [userName, onSOPage, fetchOrders]);

  // Instant feedback right after SalesOrderV3.js saves — same mechanism as InvoiceV3.js's
  // 'invoice-monitor:new-invoice' event. Opens compact, never expanded.
  useEffect(() => {
    if (!onSOPage) return;
    const handleNewOrder = (e) => {
      const { orderNo, idCompany } = e.detail || {};
      if (!orderNo) return;
      setRows((prev) => {
        if (prev.some((r) => r.orderNo === orderNo && r.idCompany === idCompany)) return prev;
        return [{ orderNo, idCompany, status: 'pending', attempts: 0, lastCheckedAt: null, pickingVisible: 'unknown', createdAt: new Date().toISOString() }, ...prev];
      });
      setOpen(true);
      setExpanded(false);
      setTimeout(fetchOrders, 8000);
    };
    window.addEventListener('so-monitor:new-order', handleNewOrder);
    return () => window.removeEventListener('so-monitor:new-order', handleNewOrder);
  }, [onSOPage, fetchOrders]);

  if (!userName || !onSOPage) return null;

  const needsAttention = rows.filter((r) => r.status === 'failed').length;
  const inProgress = rows.filter((r) => IN_PROGRESS_STATUSES.includes(r.status)).length;
  const orders = groupByOrder(rows);
  // TOOLBAR-2026-10-02 — compact view only ever shows the most recent
  // COMPACT_ORDER_LIMIT orders (already sorted newest-first by
  // groupByOrder); Expand still shows the full last-24h list.
  const visibleOrders = expanded ? orders : orders.slice(0, COMPACT_ORDER_LIMIT);

  const togglePanel = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setExpanded(false);
      fetchOrders();
    }
  };

  const handleRetryNow = async (leg) => {
    const key = `${leg.orderNo}-${leg.idCompany}`;
    setRetryingKey(key);
    try {
      await getInformationWithDataV3('/v3/so/monitor/retry-now', { userName, orderNo: leg.orderNo, idCompany: leg.idCompany });
      await fetchOrders();
    } catch (err) {
      console.log('[SOMonitorButton] retry-now error:', err);
    } finally {
      setRetryingKey(null);
    }
  };

  return (
    /* TOOLBAR-2026-10-02 — no longer self-positions (position:fixed removed);
       it's now one segment of SalesOrderV3.js's unified bottom-right toolbar
       (see so-v3-toolbar in css/table-font-boost.css), which owns the fixed
       placement. The panel below still anchors itself to the viewport
       independently (position:fixed, right:20px — same right edge as the
       toolbar) so it stays correctly placed regardless of where exactly
       this button sits inside that flex bar.
       z-index 900 (2026-09-28, client-reported) — was 9999, which sat above
       the side menu's own Bootstrap dropdowns (z-index 1000) and blocked
       navigation while the panel was open. 900 keeps it above ordinary page
       content but below any nav dropdown/offcanvas. */
    <div style={{ position: 'relative', zIndex: 900 }}>
      <button className={`so-toolbar-btn${open ? ' active' : ''}`} onClick={togglePanel}>
        <AiOutlineShopping size={18} /> <span className="so-toolbar-btn-label">Monitor SO</span>

        {needsAttention > 0 && (
          <span className="badge bg-danger position-absolute top-0 start-100 translate-middle">{needsAttention}</span>
        )}
        {needsAttention === 0 && inProgress > 0 && (
          <span className="badge bg-warning text-dark position-absolute top-0 start-100 translate-middle">{inProgress}</span>
        )}
      </button>

      {open && (
        <div
          className="card shadow p-2 mb-2 d-flex flex-column"
          style={{
            width: PANEL_WIDTH,
            position: 'fixed',
            right: 20,
            ...(expanded ? { top: 20, bottom: 90 } : { bottom: 86 }),
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong style={{ fontSize: '0.82rem' }}>
              {expanded ? 'Sales orders — last 24h' : `Sales orders — last ${Math.min(COMPACT_ORDER_LIMIT, orders.length)}`}
            </strong>
            <div className="d-flex align-items-center gap-1">
              <button type="button" className="btn btn-sm btn-secondary" style={{ color: '#fff' }} onClick={() => setExpanded((v) => !v)} title={expanded ? 'Collapse' : 'Expand'}>
                {expanded ? <AiOutlineFullscreenExit size={14} /> : <AiOutlineFullscreen size={14} />}
              </button>
              <button type="button" className="btn btn-sm btn-primary d-flex align-items-center gap-1" onClick={fetchOrders} disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : <AiOutlineReload size={14} />}
              </button>
            </div>
          </div>

          {lastFetchedAt && (
            <div className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>Last updated: {lastFetchedAt.toLocaleTimeString()}</div>
          )}

          <ul
            className="list-group"
            style={expanded ? { flex: '1 1 auto', overflowY: 'auto', minHeight: 0 } : { maxHeight: COMPACT_LIST_HEIGHT, overflowY: 'auto' }}
          >
            {loading && visibleOrders.length === 0 ? (
              <li className="list-group-item text-center text-muted" style={{ fontSize: '0.78rem', padding: '0.4rem 0.6rem' }}>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Loading...
              </li>
            ) : visibleOrders.length === 0 ? (
              <li className="list-group-item text-muted" style={{ fontSize: '0.78rem', padding: '0.4rem 0.6rem' }}>No sales orders in the last 24h</li>
            ) : (
              visibleOrders.map((order) => (
                <li key={order.orderNo} className="list-group-item" style={{ padding: '0.4rem 0.6rem', fontSize: '0.85rem' }}>
                  <div className="fw-bold mb-1" style={{ fontSize: '0.82rem' }}>{order.orderNo}</div>
                  {order.legs.map((leg) => {
                    const key = `${leg.orderNo}-${leg.idCompany}`;
                    const legInProgress = IN_PROGRESS_STATUSES.includes(leg.status);
                    return (
                      <div key={key} className="d-flex justify-content-between align-items-start mb-1 ps-2" style={{ borderLeft: '2px solid #e2e8f0' }}>
                        <div>
                          <small className="fw-semibold">{COMPANY_LABEL[leg.idCompany] || leg.idCompany}</small>
                          {' — '}
                          <small className={`text-${STATUS_CLASS[leg.status] || 'secondary'}`}>
                            {STATUS_LABEL[leg.status] || leg.status}
                            {leg.status === 'retrying' ? ` (attempt ${leg.attempts}/3)` : ''}
                          </small>
                          <br />
                          <small className="text-muted">Last checked: {formatRelativeTime(leg.lastCheckedAt)}{legInProgress ? ` · ${getNextCheckLabel(leg)}` : ''}</small>

                          {leg.idCompany === 3 && leg.status === 'confirmed' && (
                            <>
                              <br />
                              <small className={leg.pickingVisible === 'yes' ? 'text-success' : leg.pickingVisible === 'no' ? 'text-danger' : 'text-muted'}>
                                Picking: {leg.pickingVisible === 'yes' ? 'visible' : leg.pickingVisible === 'no' ? 'NOT visible — check Order Type in Sage' : 'checking...'}
                              </small>
                            </>
                          )}

                          {leg.status === 'failed' && (
                            <div className="mt-1">
                              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleRetryNow(leg)} disabled={retryingKey === key}>
                                {retryingKey === key ? (
                                  <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Retrying...</>
                                ) : (
                                  <><AiOutlineReload size={12} className="me-1" /> Retry now</>
                                )}
                              </button>
                            </div>
                          )}
                        </div>

                        {legInProgress ? (
                          <span className="spinner-border spinner-border-sm text-warning align-self-center" role="status" aria-hidden="true"></span>
                        ) : leg.status === 'confirmed' ? (
                          <span className="badge bg-success align-self-center">✓</span>
                        ) : leg.status === 'failed' ? (
                          <span className="badge bg-danger align-self-center">!</span>
                        ) : null}
                      </div>
                    );
                  })}
                </li>
              ))
            )}
          </ul>

          {needsAttention > 0 && (
            <div className="alert alert-danger mt-2 mb-0 p-2">
              <small>{needsAttention} leg(s) could not be confirmed automatically — contact support with the order number.</small>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SOMonitorButton;
